/**
 * React wrapper for Bryntum Button
 */
import React, { RefObject } from 'react';
import { AlignSpec, Base, Button, ContainerItemConfig, MaskConfig, Menu, MenuItemConfig, Rectangle, Scroller, ScrollerConfig, TabConfig, TooltipConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumButtonProps =  {
     // Configs only
    adopt?                   : HTMLElement|string
    align?                   : AlignSpec|string
    anchor?                  : boolean
    ariaDescription?         : string
    ariaLabel?               : string
    bubbleEvents?            : object
    callOnFunctions?         : boolean
    centered?                : boolean
    config?                  : object
    constrainTo?             : HTMLElement|Widget|Rectangle
    contentElementCls?       : string|object
    defaultBindProperty?     : string
    dock?                    : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?               : boolean|{
        handleSelector?: string
    }
    floating?                : boolean
    hideAnimation?           : boolean|object
    htmlCls?                 : string|object
    ignoreParentReadOnly?    : boolean
    listeners?               : object
    localeClass?             : typeof Base
    localizable?             : boolean
    localizableProperties?   : string[]
    maskDefaults?            : Partial<MaskConfig>
    masked?                  : boolean|string|Partial<MaskConfig>
    monitorResize?           : boolean
    owner?                   : Widget
    positioned?              : boolean
    preventTooltipOnTouch?   : boolean
    relayStoreEvents?        : boolean
    ripple?                  : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?             : ShadowRoot
    scrollAction?            : 'hide'|'realign'|null
    showAnimation?           : boolean|object
    showTooltipWhenDisabled? : boolean
    supportsPressedClick?    : boolean
    tab?                     : boolean|Partial<TabConfig>
    tag?                     : string
    textAlign?               : 'left'|'center'|'right'|'start'|'end'
    title?                   : string
    ui?                      : string|object
    weight?                  : number

     // Configs and properties
    alignSelf?        : string
    appendTo?         : HTMLElement|string
    badge?            : string
    behaviorType?     : 'button'|'submit'|'reset'
    cls?              : string|object
    color?            : string
    content?          : string
    dataset?          : object
    disabled?         : boolean
    extraData?        : string|number|boolean|object
    flex?             : number|string
    height?           : number|string
    hidden?           : boolean
    href?             : string
    html?             : string|((widget: Widget) => string)
    icon?             : string
    iconAlign?        : 'start'|'end'
    id?               : string
    insertBefore?     : HTMLElement|string
    insertFirst?      : HTMLElement|string
    margin?           : number|string
    maxHeight?        : string|number
    maximizeOnMobile? : number|string
    maxWidth?         : string|number
    menu?             : Widget|Record<string, Partial<MenuItemConfig>|Partial<ContainerItemConfig>>|(Partial<MenuItemConfig>|Partial<ContainerItemConfig>)[]
    menuIcon?         : string
    minHeight?        : string|number
    minWidth?         : string|number
    pressed?          : boolean
    pressedIcon?      : string
    readOnly?         : boolean
    scrollable?       : Scroller|boolean|Partial<ScrollerConfig>
    target?           : string
    text?             : string
    toggleable?       : boolean
    toggleGroup?      : string
    tooltip?          : string|Partial<TooltipConfig>
    width?            : number|string
    x?                : number
    y?                : number

     // Properties only
    anchorSize? : number[]
    type?       : string

     // Events
    onAction?         : ((event: { source: Button, event: Event }) => void)|string
    onBeforeDestroy?  : ((event: { source: Base }) => void)|string
    onBeforeHide?     : ((event: { source: Widget }) => boolean|void)|string
    onBeforeShow?     : ((event: { source: Widget|any }) => Promise<boolean|void>)|string
    onBeforeShowMenu? : ((event: { source: Button, menu: Menu }) => void)|string
    onBeforeToggle?   : ((event: { source: Button, pressed: boolean, userAction: boolean }) => boolean|void)|string
    onCatchAll?       : ((event: {[key: string]: any, type: string}) => void)|string
    onClick?          : ((event: { source: Button, event: Event }) => void)|string
    onDestroy?        : ((event: { source: Base }) => void)|string
    onFocusIn?        : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?       : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onHide?           : ((event: { source: Widget }) => void)|string
    onPaint?          : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onReadOnly?       : ((event: { readOnly: boolean }) => void)|string
    onRecompose?      : (() => void)|string
    onResize?         : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onShow?           : ((event: { source: Widget }) => void)|string
    onToggle?         : ((event: { source: Button, pressed: boolean, userAction: boolean }) => void)|string

}

export class BryntumButton extends React.Component<BryntumButtonProps> {

    static instanceClass = Button;

    static instanceName = 'Button';

    processWidgetContent = processWidgetContent;

    static configNames = [
        'adopt',
        'align',
        'anchor',
        'ariaDescription',
        'ariaLabel',
        'bubbleEvents',
        'callOnFunctions',
        'centered',
        'config',
        'constrainTo',
        'contentElementCls',
        'defaultBindProperty',
        'dock',
        'draggable',
        'floating',
        'hideAnimation',
        'htmlCls',
        'ignoreParentReadOnly',
        'listeners',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'monitorResize',
        'owner',
        'positioned',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'ripple',
        'rootElement',
        'scrollAction',
        'showAnimation',
        'showTooltipWhenDisabled',
        'supportsPressedClick',
        'tab',
        'tag',
        'textAlign',
        'title',
        'ui',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'appendTo',
        'badge',
        'behaviorType',
        'cls',
        'color',
        'content',
        'dataset',
        'disabled',
        'extraData',
        'flex',
        'height',
        'hidden',
        'href',
        'html',
        'icon',
        'iconAlign',
        'id',
        'insertBefore',
        'insertFirst',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'menu',
        'menuIcon',
        'minHeight',
        'minWidth',
        'onAction',
        'onBeforeDestroy',
        'onBeforeHide',
        'onBeforeShow',
        'onBeforeShowMenu',
        'onBeforeToggle',
        'onCatchAll',
        'onClick',
        'onDestroy',
        'onFocusIn',
        'onFocusOut',
        'onHide',
        'onPaint',
        'onReadOnly',
        'onRecompose',
        'onResize',
        'onShow',
        'onToggle',
        'pressed',
        'pressedIcon',
        'readOnly',
        'scrollable',
        'target',
        'text',
        'toggleable',
        'toggleGroup',
        'tooltip',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'type'
    ];

    // Component instance
    instance!: Button;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumButtonProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-button-container`;
        return (
            <div className={className} ref={(element) => (this.element = element!)}></div>
        );

    }
}
